import useData from "@/hooks/useData";
import useQueries from "@/hooks/useQueries";
import MainLayout from "@/shared/components/MainLayout";
import DatePicker from "@/shared/components/ui/DatePicker";
import { Dropdown, DropdownItem } from "@/shared/components/ui/Dropdown";
import Header from "@/shared/components/ui/Header";
import Loader from "@/shared/components/ui/Loader";
import { API_URL } from "@/shared/utils/API_URL";
import { buildQuery } from "@/shared/utils/buildQuery";
import { getDate } from "@/shared/utils/getDate";
import { motion } from 'framer-motion';
import { ListOrdered } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

const AccountStatement = () => {
    const { t } = useTranslation();
    const queries = useQueries();
    const { id, name } = useParams();

    const [date, setDate] = useState({
        from: getDate(),
        to: getDate()
    });

    const accountStatementQuery = queries
        ? buildQuery(queries?.accountStatementQuery,
            { startDate: `${date.from}`, accountId: id }
        )
        : '';

    const { fetchedData, loading } = useData({
        q: accountStatementQuery,
        apiUrl: API_URL,
        dependency: [date.from, date.to, accountStatementQuery],
    });
    const accountStatement = fetchedData?.data?.result || [];


    const handleQuickSelect = (type: string,) => {
        const formatDate = (date: any) => {
            const year = date?.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }

        let start = new Date();
        let end = new Date();
        const now = new Date();
        if (type == 'today') {
            start = new Date();
            end = new Date();
        }
        if (type == 'yesterday') {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            start = yesterday;
            end = yesterday;
        }
        else if (type === 'current_month') {

            start = new Date(now.getFullYear(), now.getMonth(), 1);
            end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }
        else if (type === 'last_month') {
            start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            end = new Date(now.getFullYear(), now.getMonth(), 0);
        }
        setDate(prev => ({ ...prev, from: formatDate(start) }));
        setDate(prev => ({ ...prev, to: formatDate(end) }));
    }

    return (
        <MainLayout>
            <Header title={t("Account statement")} info={accountStatementQuery} />

            <main className="px-4 py-8 min-h-[calc(100vh-100px)] flex flex-col items-center justify-center">
                <div className="w-full max-w-6xl">
                    <div className="mb-6 w-full max-w-6xl">
                        <div className="flex flex-col justify-between xl:flex-row xl:items-end items-stretch gap-4 w-full">
                            <div className="grid grid-cols-2 gap-2 md:gap-4">
                                <div className="flex flex-col gap-1 min-w-0">
                                    <Dropdown label={t("start_date")}>
                                        <DropdownItem onClick={() => handleQuickSelect('today')}>{t("today")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('yesterday')}>{t("yesterday")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('current_month')}>{t("current month")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('last_month')}>{t("before month")}</DropdownItem>
                                    </Dropdown>
                                    <DatePicker
                                        id="date-picker"
                                        label=''
                                        placeholder={t("Select a date")}
                                        onChange={(_, currentDateString) => setDate((prev) => ({ ...prev, from: currentDateString }))}
                                        defaultDate={date.from}
                                    />
                                </div>
                                {/* <div className="flex flex-col gap-1 min-w-0">
                                    <Dropdown label={t("end_date")}>
                                        <DropdownItem onClick={() => handleQuickSelect('today')}>{t("today")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('yesterday')}>{t("yesterday")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('current_month')}>{t("current month")}</DropdownItem>
                                        <DropdownItem onClick={() => handleQuickSelect('last_month')}>{t("before month")}</DropdownItem>
                                    </Dropdown>
                                    <DatePicker
                                        id="date-picker1"
                                        label=""
                                        placeholder={t("Select a date")}
                                        onChange={(_, currentDateString) => setDate((prev) => ({ ...prev, to: currentDateString }))}
                                        defaultDate={date.to}
                                    />
                                </div> */}
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex justify-center py-20">
                            <Loader />
                        </div>
                    ) : (
                        <div className="bg-white rounded-[1rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">

                            <div className="overflow-x-auto">
                                <table className="w-full text-right border-collapse">
                                    <thead>
                                        <tr className="text-slate-400 uppercase text-[11px] font-black tracking-widest border-b border-slate-100 bg-slate-50/30">
                                            <th className="px-6 py-4">{t("transaction_id")}</th>
                                            <th className="px-6 py-4">{t("transaction_type")}</th>
                                            <th className="px-6 py-4">{t("transaction_value")}</th>
                                            <th className="px-6 py-4">{t("trnsaction_date")}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {accountStatement.length > 0 ? (
                                            accountStatement.map((item: any, index: number) => {
                                                const isInitialBalance = item?.transaction_id?.toLowerCase() == 'old';
                                                return (
                                                    <motion.tr
                                                        key={index}
                                                        initial={{ opacity: 0, y: 10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ delay: index * 0.03 }}
                                                        className={`hover:bg-violet-50/30 transition-colors group`}
                                                    >
                                                        {isInitialBalance ?
                                                            (
                                                                <td colSpan={2} className="px-6 py-5 text-center text-sm">
                                                                    {t("رصيد بداية المدة")}
                                                                </td>
                                                            ) : (
                                                                <>
                                                                    <td className="px-6 py-4 text-sm font-medium text-slate-700">
                                                                        {item?.transaction_id || "---"}
                                                                    </td>
                                                                    <td className="px-6 py-4 text-sm text-slate-600">
                                                                        {item?.transaction_type || "---"}
                                                                    </td>
                                                                </>
                                                            )
                                                        }

                                                        <td className="px-6 py-4 text-sm text-slate-500">
                                                            {item?.transaction_value || "---"}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm">
                                                            {item?.trnsaction_date || "---"}
                                                        </td>

                                                    </motion.tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="px-6 py-20 text-center">
                                                    <div className="flex flex-col items-center gap-3 opacity-20">
                                                        <ListOrdered size={48} />
                                                        <p className="font-black uppercase tracking-widest text-sm">{t("No Data Found")}</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>

                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </main>

        </MainLayout>
    )
}

export default AccountStatement;